"use client";

import type { ContentfulAssetFragment, Maybe } from "@server/gql/graphql";
import type { ImageProps as NextImageProps } from "next/image";
import Image from "next/image";
import type { ComponentPropsWithoutRef } from "react";

import { breakpoints } from "@/config/breakpoints";
import { cn } from "@/lib/utils";
import { getContentfulImageProps } from "@/utils/getContentfulImageProps";

type ContentfulPictureProps = ComponentPropsWithoutRef<"picture"> & {
  imageProps?: ImageProps;
  desktopImage?: Maybe<ContentfulAssetFragment>;
  mobileImage?: Maybe<ContentfulAssetFragment>;
  mobileBreakpoint?: (typeof breakpoints)[keyof typeof breakpoints];
};

type ImageProps = Omit<NextImageProps, "src" | "alt">;

const ContentfulPicture = ({
  className,
  imageProps: { className: imageClassName, ...imageProps } = {},
  desktopImage,
  mobileImage,
  mobileBreakpoint = breakpoints.lg,
  ...props
}: ContentfulPictureProps) => {
  const defaultImageUrl = desktopImage?.url ?? mobileImage?.url;

  if (!defaultImageUrl) {
    return null;
  }

  const defaultImageWidth =
    desktopImage?.width ?? mobileImage?.width ?? undefined;
  const defaultImageHeight =
    desktopImage?.height ?? mobileImage?.height ?? undefined;
  const defaultImageAlt =
    desktopImage?.description ??
    mobileImage?.description ??
    desktopImage?.title ??
    mobileImage?.title ??
    "";

  const mobileImageProps = getContentfulImageProps(mobileImage);

  return (
    <picture className={cn("relative", className)} {...props}>
      {mobileImageProps?.srcSet && (
        <source
          srcSet={mobileImageProps.srcSet}
          media={`(max-width: ${mobileBreakpoint}px)`}
        />
      )}
      <Image
        className={cn("object-cover", imageClassName)}
        src={defaultImageUrl}
        width={defaultImageWidth}
        height={defaultImageHeight}
        alt={defaultImageAlt}
        {...imageProps}
      />
    </picture>
  );
};

ContentfulPicture.displayName = "ContentfulPicture";

export default ContentfulPicture;
